@if (loader.message != null) {
  <div class="loader-class" aria-live="polite">
    <mat-spinner diameter="28"></mat-spinner>
    <div class="loader-message">{{ loader.message }}</div>
  </div>
}

@if (request) {
  <section class="panel">
    <div class="detail-header">
      <div>
        <h3>#{{ request.feedback_id }} - {{ request.title }}</h3>
        <div class="meta-badges">
          <span class="status-pill" [class]="store.statusClassMap[getStatusLabel()]">{{ getStatusLabel() }}</span>
          <span class="team-badge">{{ request.assigned_team_name }}</span>
          <span class="criticality-badge" [class]="'criticality-' + getCriticalityLabel().toLowerCase()">{{ getCriticalityLabel() }}</span>
          <button class="likes-badge" (click)="toggleLike()" [class.liked]="request.is_liked"
            [matTooltip]="getLikedUsersTooltip()" matTooltipClass="liked-users-tooltip"
            [matTooltipDisabled]="loadingLikers" [disabled]="loader.isLoading || loadingLikers">
            <span class="likes-icon">👍</span>
            <span class="likes-action-text">Like</span>
            <span class="likes-count">{{ request.likedByUsers!.length }}</span>
          </button>
          @if (currentRole === SupportHubStore.ROLE_USER) {
            <button class="likes-badge follow-badge" (click)="toggleFollowTicket()" [class.followed]="request.is_following"
              [disabled]="loader.isLoading || loadingFollowers">
              <mat-icon class="follow-icon">{{ request.is_following ? 'notifications_off' : 'notifications' }}</mat-icon>
              <span class="likes-action-text">{{ request.is_following ? 'Unfollow' : 'Follow' }}</span>
            </button>
          }
          @if (canEditTicket()) {
            <button class="link-btn" type="button" (click)="editTicket()">Edit ticket</button>
          }
        </div>
      </div>
      <button class="link-btn" (click)="backToList()">Back To List</button>
    </div>

    <p class="description">{{ request.description }}</p>

    <!-- Key Details Section -->
    <div class="details-section">
      <h4>Key Details</h4>
      <div class="details-grid">
        <div class="detail-item">
          <span class="detail-label">Use Case</span>
          <span class="detail-value">{{ getUseCaseLabel() }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Category</span>
          <span class="detail-value">{{ getWorkCategoryLabel() }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Request Type</span>
          <span class="detail-value">{{ getRequestTypeLabel() }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Application</span>
          <span class="detail-value">{{ getApplicationLabel() }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Assigned Team</span>
          <span class="detail-value">{{ request.assigned_team_name }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Company</span>
          <span class="detail-value">{{ request.company }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Plant</span>
          <span class="detail-value">{{ request.plant }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Shop</span>
          <span class="detail-value">{{ request.shop }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Submitted</span>
          <span class="detail-value">{{ request.creation_date | date: dateFormat }}</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">Submitter</span>
          <span class="detail-value">{{ request.requested_user_name }}</span>
        </div>
      </div>
    </div>

    <!-- Unified Actions Section -->
    <div id="actions" class="action-panel actions-section">
      <h4>Workflow</h4>
      <div class="action-content">
        <div class="action-group">
          @if (canAssignTeamMember()) {
            <label for="team-assignee">Assigned To</label>
            <div class="dropdown-row">
              <select id="team-assignee" [(ngModel)]="teamAssignee" [disabled]="loadingValidationUsers || isClosedOrCompleted">
                <option [ngValue]="null" disabled>Select team member</option>
                @for (user of teamAssignees; track user.id) {
                  <option [ngValue]="user.id">{{ user.user_name }}</option>
                }
              </select>
            </div>
          }
          @else {
            <span class="detail-label">Assigned To</span>
            <span class="detail-value">{{ request.assigned_user_name || '-' }}</span>
          }
        </div>

        <div class="action-group">
          @if (canUpdateAnyStatus()) {
            <label for="ticket-status">Ticket Status</label>
            <div class="dropdown-row">
              <select id="ticket-status" [(ngModel)]="selectedStatusId" [disabled]="isClosedOrCompleted">
                @for (status of store.statusOptions; track status.id) {
                  <option [ngValue]="status.id">{{ status.name }}</option>
                }
              </select>
            </div>
          }
          @else {
            <span class="detail-label">Ticket Status</span>
            <span class="detail-value">{{ getStatusLabel() }}</span>
          }
        </div>

        <div class="action-group">
          @if (canAssignValidator()) {
            <label for="validation-assignee">Validation Assignee</label>
            <div class="dropdown-row">
              <select id="validation-assignee" [(ngModel)]="validationAssignee" [disabled]="loadingValidationUsers || isClosedOrCompleted">
                <option [ngValue]="null" disabled>Select user</option>
                @for (user of validationUsers; track user.id) {
                  <option [ngValue]="user.id">{{ user.user_name }}</option>
                }
              </select>
            </div>
          }
          @else {
            <span class="detail-label">Validation Assignee</span>
            <span class="detail-value">{{ request.validated_user_name || '-' }}</span>
          }
        </div>

        <div class="action-group">
          @if (isAdminRole()) {
            <label for="ticket-version">Version</label>
            <div class="dropdown-row">
              <select id="ticket-version" [(ngModel)]="selectedVersionId" [disabled]="loadingVersions || isClosedOrCompleted">
                <option [ngValue]="null" disabled>Select version</option>
                @for (version of versionsList; track version.version_id) {
                  <option [ngValue]="version.version_id">{{ version.version_name }}</option>
                }
              </select>
            </div>
          }
          @else {
            <span class="detail-label">Version</span>
            <span class="detail-value">{{ request.version || '-' }}</span>
          }
        </div>

        <div class="action-group">
          @if (isAdminRole()) {
            <label for="ticket-priority">Priority</label>
            <div class="dropdown-row">
              <select id="ticket-priority" [(ngModel)]="selectedPriorityId" [disabled]="isClosedOrCompleted">
                <option [ngValue]="null" disabled>Select priority</option>
                @for (priority of store.priorityOptions; track priority.id) {
                  <option [ngValue]="priority.id">{{ priority.name }}</option>
                }
              </select>
            </div>
          }
          @else {
            <span class="detail-label">Priority</span>
            <span class="detail-value">{{ getPriorityLabel() }}</span>
          }
        </div>

        @if (currentRole !== SupportHubStore.ROLE_USER) {
          <div class="action-group">
            <div class="div-actions-button">
              <button class="btn btn-primary" (click)="applyAllChanges()" [disabled]="!hasChanges()">Apply Changes</button>
              <button class="btn btn-secondary" (click)="resetAllChanges()" [disabled]="!hasChanges()">Reset Changes</button>
            </div>
          </div>
        }
      </div>
    </div>
    
    <div class="action-panel actions-section">
      <h4>{{ currentRole }} Actions</h4>
      <div class="action-content role-actions-content">
        <div class="action-group">
          <div class="dropdown-row div-role-buttons">
            @if (currentRole !== SupportHubStore.ROLE_USER) {
              <button class="btn btn-secondary" (click)="showRequestClarificationPanel = !showRequestClarificationPanel" [disabled]="buttonRequestClarificationDisabled || isClosedOrCompleted" >
                Request Clarification
              </button>
              <button [hidden]="true" class="btn btn-secondary" (click)="showRequestValidationPanel = !showRequestValidationPanel" [disabled]="!canSendForValidation()">
                Send Validation Note
              </button>
              <button [hidden]="true" class="btn btn-secondary" (click)="markDeploying()" [disabled]="!store.isStatus(request.status_id, store.statusIds.UAT_COMPLETE)">
                Mark Deploying
              </button>
              <button [hidden]="true" class="btn btn-secondary" (click)="markComplete()" [disabled]="!store.isStatus(request.status_id, store.statusIds.DEPLOYING)">
                Mark Complete
              </button>
            }
            @else {
              <button class="btn btn-secondary" (click)="toggleLike()"
                [disabled]="loader.isLoading || loadingLikers">
                {{ request.is_liked ? 'Remove like' : 'Like' }}
              </button>
              <button class="btn btn-secondary" (click)="toggleFollowTicket()"
                [disabled]="loader.isLoading || loadingFollowers">
                {{ request.is_following ? 'Unfollow' : 'Follow' }} Ticket
              </button>
            }
            @if (canSubmitClarification() && !disabledButtonSubmit) {
              <button class="btn btn-secondary" (click)="showSubmitClarificationPanel = !showSubmitClarificationPanel">
                Submit Clarification
              </button>
            }
            @if (canConfirmValidation()) {
              <button class="btn btn-secondary" (click)="showConfirmValidationPanel = !showConfirmValidationPanel">
                Confirm Validation
              </button>
            }
          </div>
        </div>

        @if (currentRole !== SupportHubStore.ROLE_USER && showRequestClarificationPanel) {
          <div class="action-group">
            <label for="clarification-note">Clarification Note *</label>
            <textarea id="clarification-note" [(ngModel)]="clarificationNote" rows="3" placeholder="Tell the requester what needs clarification."></textarea>
            
            @if (isDeveloperRole()) {
              <label for="ticket-priority">Send a note to the user</label>
              <div class="dropdown-row">
                <select id="ticket-priority" [(ngModel)]="initialAssignedUserId"  [disabled]="loadingClarificationUsers">
                  <option [ngValue]="null" disabled>Select user</option>
                  @for (user of clarificationUsers; track user.id) {
                    <option [ngValue]="user.id">{{ user.user_name }}</option>
                  }
                </select>
              </div>
            }
              <div class="dropdown-row">
              <button class="btn btn-primary" (click)="requestClarification()" [disabled]="!clarificationNote.trim()">
                Send Request
              </button>
            </div>
          </div>
        }

        @if (currentRole !== SupportHubStore.ROLE_USER && showRequestValidationPanel) {
          <div [hidden]="true" class="action-group">
            <label for="validation-note">Validation Note *</label>
            <textarea id="validation-note" [(ngModel)]="validationNote" rows="3" placeholder="Add validation context for the reviewer."></textarea>
            @if (isDeveloperRole()) {
              <label for="ticket-priority">Send a note to the user</label>
              <div class="dropdown-row">
                <select id="ticket-priority" [(ngModel)]="selectedUserNoteId"  [disabled]="loadingClarificationUsers">
                  <option [ngValue]="null" disabled>Select user</option>
                  @for (user of clarificationUsers; track user.id) {
                    <option [ngValue]="user.id">{{ user.user_name }}</option>
                  }
                </select>
              </div>
            }
            <div class="dropdown-row">
              <button class="btn btn-primary" (click)="sendForValidation()" [disabled]="!validationNote.trim() || !canSendForValidation()">
                Send To Validation
              </button>
            </div>
          </div>
        }

        @if (canSubmitClarification() && showSubmitClarificationPanel) {
          <div class="action-group">
            <label for="clarification-response">Clarification Response</label>
            <textarea id="clarification-response" [(ngModel)]="clarificationNote" rows="3" placeholder="Reply with the additional context needed."></textarea>
            @if (isDeveloperRole()) {
              <label for="ticket-priority">Send a note to the user</label>
              <div class="dropdown-row">
                <select id="ticket-priority" [(ngModel)]="selectedUserNoteId"  [disabled]="loadingClarificationUsers">
                  <option [ngValue]="null" disabled>Select user</option>
                  @for (user of clarificationUsers; track user.id) {
                    <option [ngValue]="user.id">{{ user.user_name }}</option>
                  }
                </select>
              </div>
            }
            <div class="dropdown-row">
              <button class="btn btn-primary" (click)="submitClarificationResponse()" [disabled]="!clarificationNote.trim()">Send Response</button>
            </div>
          </div>
        }

        @if (canConfirmValidation() && showConfirmValidationPanel) {
          <div class="action-group">
            <label for="validation-feedback">Validation Feedback</label>
            <textarea id="validation-feedback" [(ngModel)]="validationNote" rows="3" placeholder="Optional feedback if you need more changes."></textarea>
            <div class="dropdown-row">
              <button class="btn btn-primary" (click)="approveValidation()">Approve Release</button>
              <button class="btn btn-secondary" (click)="rejectValidation()" [disabled]="!validationNote.trim()">Reject And Return</button>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Attachments Section -->
    @if (feedbackFiles.length > 0 || canManageAttachments()) {
      <div class="attachments-section">
        <h4>Attachments</h4>
        @if (canManageAttachments()) {
          <div id="attachment-div">
            <p-fileupload #uploader mode="advanced" chooseLabel="Choose Files" 
              [disabled]="loader.isLoading || feedbackFiles.length >= store.maxAttachmentCount || isClosedOrCompleted"
              [maxFileSize]="store.maxFileSizeMb * 1024 * 1024"
              [multiple]="true" [customUpload]="true" [previewWidth]="0"
              [fileLimit]="store.maxAttachmentCount" showUploadButton="false" showCancelButton="false"
              (onSelect)="onAttachmentSelected($event)"
              (onRemove)="onAttachmentRemoved($event)">
            </p-fileupload>

            <div class="attachment-help-text">
              Maximum {{ store.maxAttachmentCount }} files, {{ store.maxFileSizeMb }} MB each.
            </div>
          </div>
        }

        @if (feedbackFiles.length > 0) {
          <div class="attachment-file-list">
            @for (fileInfo of feedbackFiles; track (fileInfo.id ?? ($index + fileInfo.file_name))) {
              @if (fileInfo.id) {
                <div class="attachment-file-row">
                  <div class="attachment-preview-wrapper">
                    @if (isImageAttachment(fileInfo) && getAttachmentPreviewUrl(fileInfo); as imageUrl) {
                      <img class="attachment-preview-image" [src]="imageUrl" [alt]="fileInfo.file_name" loading="lazy" />
                    }
                    @else {
                      <div class="attachment-preview-fallback">No Preview</div>
                    }
                  </div>
                  <div class="attachment-file-details">
                    <span class="attachment-file-name" [title]="fileInfo.file_name">{{ fileInfo.file_name }}</span>
                    <span class="attachment-file-size">{{ fileInfo.file_kb_size }} KB</span>
                  </div>
                  <div class="attachment-actions">
                    <button type="button" class="attachment-icon"
                      (click)="downloadAttachment($index)"
                      aria-label="Download attached file"
                      title="Download attachment"
                      [disabled]="loader.isLoading">
                      ⬇
                    </button>
                    @if (canManageAttachments()) {
                      <button type="button" class="attachment-icon attachment-icon--delete"
                        (click)="deleteAttachment($index)"
                        aria-label="Delete attached file"
                        title="Delete attachment"
                        [disabled]="loader.isLoading">
                        ✕
                      </button>
                    }
                  </div>
                </div>
              }
            }
          </div>
        }
      </div>
    }

    <!-- Thread Tabs -->
    <div id="threads" class="thread-tabs-container">
      <div class="thread-tab-bar" role="tablist">
        <button class="thread-tab"
          [class.active]="activeTab === 'comments'"
          (click)="activeTab = 'comments'" role="tab"
          [attr.aria-selected]="activeTab === 'comments'">
          Comments
          @if (publicCommentsTotalCount > 0) {
            <span class="tab-badge">{{ publicCommentsTotalCount }}</span>
          }
        </button>
        @if (canUseTeamThread()) {
          <button class="thread-tab thread-tab--private"
            [class.active]="activeTab === 'teamthread'"
            (click)="activeTab = 'teamthread'" role="tab"
            [attr.aria-selected]="activeTab === 'teamthread'">
            Team Thread
            <span class="tab-badge tab-badge--private">Private</span>
            @if (privateCommentsTotalCount > 0) {
              <span class="tab-badge">{{ privateCommentsTotalCount }}</span>
            }
          </button>
        }
        <button class="thread-tab"
          [class.active]="activeTab === 'clarifications'"
          (click)="activeTab = 'clarifications'" role="tab"
          [attr.aria-selected]="activeTab === 'clarifications'">
          Clarifications
          @if (clarificationsTotalCount > 0) {
            <span class="tab-badge">{{ clarificationsTotalCount }}</span>
          }
        </button>
        <button class="thread-tab"
          [class.active]="activeTab === 'activity'"
          (click)="activeTab = 'activity'" role="tab"
          [attr.aria-selected]="activeTab === 'activity'">
          Activity Log
          @if (activityLogsTotalCount > 0) {
            <span class="tab-badge">{{ activityLogsTotalCount }}</span>
          }
        </button>
      </div>

      <div class="thread-tab-content" role="tabpanel">
        <!-- Comments Tab -->
        @if (activeTab === 'comments') {
          <div class="comment-compose">
            <textarea [(ngModel)]="publicComment"
              [disabled]="isCommentSectionBusy"
              placeholder="Write a comment…" rows="3">
            </textarea>
            <div class="compose-actions">
              <button class="btn btn-primary" (click)="addPublicComment()"
                [disabled]="!publicComment.trim() || isCommentSectionBusy">
                Post Comment
              </button>
            </div>
          </div>

          @if (loadingPublicComments) {
            <div class="comment-loading">Loading comments...</div>
          }

          <ul class="thread-feed">
            @for (entry of request.publicComments; track entry.id) {
              <li class="thread-entry">
                <div class="thread-entry-meta">
                  <span class="thread-author">{{ entry.user_name }}</span>
                  <span class="thread-time">{{ entry.modified_date | date: dateFormat }}</span>
                </div>
                @if (editingPublicCommentId === entry.id) {
                  <div class="thread-edit-box">
                    <textarea [(ngModel)]="editingPublicCommentText" rows="3" [disabled]="isCommentSectionBusy"></textarea>
                    <div class="thread-entry-actions">
                      <button class="btn btn-primary" (click)="saveEditComment(entry)"
                        [disabled]="!editingPublicCommentText.trim() || isCommentSectionBusy">
                        Save
                      </button>
                      <button class="btn btn-secondary" (click)="cancelEditComment(entry)" [disabled]="isCommentSectionBusy">
                        Cancel
                      </button>
                    </div>
                  </div>
                }
                @else {
                  <p class="thread-body">{{ entry.comment }}</p>
                  <div class="thread-entry-actions">
                    <button class="link-btn" (click)="startEditComment(entry)" [disabled]="isCommentSectionBusy">Edit</button>
                    <button class="link-btn" (click)="deleteComment(entry)" [disabled]="isCommentSectionBusy">Delete</button>
                  </div>
                }
              </li>
            }
            @if (!loadingPublicComments && request.publicComments!.length === 0) {
              <li class="tab-empty">No comments yet. Be the first to add one.</li>
            }
          </ul>

          @if (canPaginatePublicComments) {
            <div class="thread-pagination">
              <button class="btn btn-secondary" (click)="goToPreviousPublicCommentsPage()"
                [disabled]="publicCommentsPage <= 1 || isCommentSectionBusy">
                Previous
              </button>
              <span class="page-meta">Page {{ publicCommentsPage }} of {{ publicCommentsTotalPages }}</span>
              <button class="btn btn-secondary" (click)="goToNextPublicCommentsPage()"
                [disabled]="publicCommentsPage >= publicCommentsTotalPages || isCommentSectionBusy">
                Next
              </button>
            </div>
          }
        }

        <!-- Team Thread Tab (Developer & Super Admin only) -->
        @if (activeTab === 'teamthread' && canUseTeamThread()) {
          <div class="comment-compose">
            <textarea [(ngModel)]="teamThreadNote"
              [disabled]="isCommentSectionBusy"
              placeholder="Write a private team note…" rows="3">
            </textarea>
            <div class="compose-actions">
              <button class="btn btn-primary" (click)="addTeamThreadNote()"
                [disabled]="!teamThreadNote.trim() || isCommentSectionBusy">
                Post Team Note
              </button>
            </div>
          </div>

          @if (loadingPrivateNotes) {
            <div class="comment-loading">Loading team notes...</div>
          }

          <ul class="thread-feed">
            @for (entry of request.teamThreadComments; track entry.id) {
              <li class="thread-entry thread-entry--private">
                <div class="thread-entry-meta">
                  <span class="thread-author">{{ entry.user_name }}</span>
                  <span class="thread-role-badge">Private</span>
                  <span class="thread-time">{{ entry.modified_date | date: dateFormat }}</span>
                </div>
                @if (editingPrivateCommentId === entry.id) {
                  <div class="thread-edit-box">
                    <textarea [(ngModel)]="editingPrivateCommentText" rows="3" [disabled]="isCommentSectionBusy"></textarea>
                    <div class="thread-entry-actions">
                      <button class="btn btn-primary" (click)="saveEditComment(entry)"
                        [disabled]="!editingPrivateCommentText.trim() || isCommentSectionBusy">
                        Save
                      </button>
                      <button class="btn btn-secondary" (click)="cancelEditComment(entry)" [disabled]="isCommentSectionBusy">
                        Cancel
                      </button>
                    </div>
                  </div>
                }
                @else {
                  <p class="thread-body">{{ entry.comment }}</p>
                  <div class="thread-entry-actions">
                    <button class="link-btn" (click)="startEditComment(entry)" [disabled]="isCommentSectionBusy">Edit</button>
                    <button class="link-btn" (click)="deleteComment(entry)" [disabled]="isCommentSectionBusy">Delete</button>
                  </div>
                }
              </li>
            }
            @if (!loadingPrivateNotes && request.teamThreadComments!.length === 0) {
              <li class="tab-empty">No team notes yet.</li>
            }
          </ul>

          @if (canPaginatePrivateComments) {
            <div class="thread-pagination">
              <button class="btn btn-secondary" (click)="goToPreviousPrivateCommentsPage()"
                [disabled]="privateCommentsPage <= 1 || isCommentSectionBusy">
                Previous
              </button>
              <span class="page-meta">Page {{ privateCommentsPage }} of {{ privateCommentsTotalPages }}</span>
              <button class="btn btn-secondary" (click)="goToNextPrivateCommentsPage()"
                [disabled]="privateCommentsPage >= privateCommentsTotalPages || isCommentSectionBusy">
                Next
              </button>
            </div>
          }
        }

        <!-- Clarifications Tab -->
         @if (activeTab === 'clarifications') {
          @if (loadingClarifications) {
            <div class="comment-loading">Loading clarification history...</div>
          }

          @if (!loadingClarifications) {
            <ul class="thread-feed">
              @for (entry of paginatedClarifications; track entry.created_date) {
                <li class="thread-entry thread-entry--activity">
                  <div class="thread-entry-meta">
                    <span class="thread-author author-request">{{ entry.request.user_name }}</span>
                    <span class="thread-role-badge">Clarification</span>
                    <span class="thread-time">{{ entry.created_date | date: dateFormat }}</span>
                  </div>

                  @if (entry.request.note) {
                    <p class="thread-body thread-body--multiline"><strong>Request:</strong> {{ entry.request.note }}</p>
                  }

                  <div class="thread-entry-meta">
                    <span class="thread-author author-response">{{ entry.response.user_name }}</span>
                    <span class="thread-time">{{ entry.response?.date ?? ''| date: dateFormat }}</span>
                  </div>
                  @if (entry.response.note) {
                    <p class="thread-body thread-body--multiline"><strong>Response:</strong> {{ entry.response.note }}</p>
                  }
                </li>
              }
              @if (paginatedClarifications.length === 0) {
                <li class="tab-empty">No clarification history yet.</li>
              }
            </ul>
          }


          @if (canPaginateClarifications) {
            <div class="thread-pagination">
              <button class="btn btn-secondary" (click)="goToPreviousClarificationsPage()"
                [disabled]="clarificationsPage <= 1 || loadingClarifications">
                Previous
              </button>
              <span class="page-meta">Page {{ clarificationsPage }} of {{ clarificationsTotalPages }}</span>
              <button class="btn btn-secondary" (click)="goToNextClarificationsPage()"
                [disabled]="clarificationsPage >= clarificationsTotalPages || loadingClarifications">
                Next
              </button>
            </div>
          }
        }

        <!-- Activity Log Tab -->
        @if (activeTab === 'activity') {
          @if (loadingActivityLogs) {
            <div class="comment-loading">Loading activity logs...</div>
          }

          <ul class="thread-feed">
            @for (entry of request.activityLog; track entry.log_date + entry.action + entry.user_email) {
              <li class="thread-entry thread-entry--activity">
                <div class="thread-entry-meta">
                  <span class="thread-author">{{ entry.user_name }}</span>
                  <span class="thread-role-badge">{{ entry.action }}</span>
                  <span class="thread-time">{{ entry.log_date | date: dateFormat }}</span>
                </div>
                <p class="thread-body thread-body--multiline">{{ entry.description }}</p>
              </li>
            }
            @if (!loadingActivityLogs && request.activityLog.length === 0) {
              <li class="tab-empty">No activity recorded yet.</li>
            }
          </ul>

          @if (canPaginateActivityLogs) {
            <div class="thread-pagination">
              <button class="btn btn-secondary" (click)="goToPreviousActivityLogsPage()"
                [disabled]="activityLogsPage <= 1 || loadingActivityLogs">
                Previous
              </button>
              <span class="page-meta">Page {{ activityLogsPage }} of {{ activityLogsTotalPages }}</span>
              <button class="btn btn-secondary" (click)="goToNextActivityLogsPage()"
                [disabled]="activityLogsPage >= activityLogsTotalPages || loadingActivityLogs">
                Next
              </button>
            </div>
          }
        }
      </div>
    </div>
  </section>
}
@else if (!hasRouteRequestId) {
  <section class="panel">
    <h3>Request not found</h3>
    <a routerLink="/support-hub/all-tickets" [queryParams]="store.getUseCaseQueryParams()" class="link-btn">Back To List</a>
  </section>
}